import { type FC, useMemo } from 'react';
import { useTranslation } from '@pypestream/translations';

import { ConfigPanel } from '../../components/table/src/config-panel';

import type { PerfMetricRow } from './types';

const NS = 'pro-studio/performance';

export interface PerfDetailDrawerProps {
  row: PerfMetricRow | null;
  onClose: () => void;
}

/**
 * Side-panel for a single metric row. Uses the same `ConfigPanel` building
 * block as the workflows page's "Agents" panel (`supervisor-panel.tsx`) —
 * gives us the standard title bar, top-right close button, scrollable body,
 * and consistent styling without inventing a new visual language.
 */
export const PerfDetailDrawer: FC<PerfDetailDrawerProps> = ({
  row,
  onClose,
}) => {
  const [t] = useTranslation();
  const formatted = useMemo(() => {
    if (!row) return '';
    return JSON.stringify(row.settings ?? {}, null, 2);
  }, [row]);

  if (!row) return null;

  const ci = row.settings?.__meta?.ci;
  const title = t(`${NS}:detail.title`, {
    scenario: row.settings?.scenario ?? row.type,
    size: row.settings?.size ?? '—',
  });
  const dtStyle = { color: 'var(--ps-color-text-subtle, #6b7280)' };

  return (
    <ConfigPanel title={title} onClose={onClose}>
      <dl
        style={{
          margin: 0,
          marginBottom: 16,
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '4px 16px',
          fontSize: 12,
        }}
        data-testid="ps-perf-detail-panel"
      >
        <dt style={dtStyle}>{t(`${NS}:detail.labels.id`)}</dt>
        <dd style={{ fontFamily: 'monospace', margin: 0 }}>{row.id}</dd>
        <dt style={dtStyle}>{t(`${NS}:detail.labels.createdAt`)}</dt>
        <dd style={{ margin: 0 }}>
          {new Date(row.createdAt).toLocaleString()}
        </dd>
        {ci?.sha && (
          <>
            <dt style={dtStyle}>{t(`${NS}:detail.labels.commit`)}</dt>
            <dd style={{ fontFamily: 'monospace', margin: 0 }}>{ci.sha}</dd>
          </>
        )}
        {ci?.branch && (
          <>
            <dt style={dtStyle}>{t(`${NS}:detail.labels.branch`)}</dt>
            <dd style={{ margin: 0 }}>{ci.branch}</dd>
          </>
        )}
        {ci?.prNumber != null && (
          <>
            <dt style={dtStyle}>{t(`${NS}:detail.labels.pr`)}</dt>
            <dd style={{ margin: 0 }}>#{ci.prNumber}</dd>
          </>
        )}
        {ci?.runId && (
          <>
            <dt style={dtStyle}>{t(`${NS}:detail.labels.runId`)}</dt>
            <dd style={{ margin: 0 }}>{ci.runId}</dd>
          </>
        )}
      </dl>

      <pre
        style={{
          margin: 0,
          padding: 12,
          background: 'var(--ps-color-bg-subtle, #f9fafb)',
          borderRadius: 4,
          fontSize: 11,
          lineHeight: 1.4,
          overflow: 'auto',
        }}
      >
        {formatted}
      </pre>
    </ConfigPanel>
  );
};
